import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/content';
import type { ContentItem } from '@/types/content';
import { formatViews } from '@/lib/format';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const allItems = movies as ContentItem[];
  const item = allItems.find((entry) => `${entry.id}` === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo(
    item ? `${item.title} - 91大神详情介绍` : '影视详情 - 91大神影视大全',
    item
      ? `${item.title}（${item.year}·${item.region}）剧情简介与详细信息，${item.genres.join('、')}题材，尽在91大神影视大全。`
      : '影视作品详情介绍。',
  );

  const recommended = useMemo(() => {
    if (!item) return [];
    return allItems
      .filter((entry) => entry.id !== item.id && entry.type === item.type)
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }, [allItems, item]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    if (!item) return;
    const nowFavorite = toggleFavorite(item.id);
    showToast(nowFavorite ? '已加入本地收藏（演示）' : '已取消本地收藏（演示）');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        showToast('链接已复制（演示）');
      } else {
        showToast(`分享链接：${url}（演示）`);
      }
    } catch {
      showToast('复制失败，请手动复制地址栏链接（演示）');
    }
  };

  if (!item) {
    return (
      <div className="min-h-screen w-full bg-background-100 flex flex-col">
        <SiteHeader />
        <main className="w-full flex-1 flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading font-semibold text-xl text-foreground-900">
            未找到该影视内容
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            该内容可能已被移除，返回首页继续浏览其他影视。
          </p>
          <Link
            to="/"
            className="mt-6 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const fav = isFavorite(item.id);

  return (
    <div className="min-h-screen w-full bg-background-100 flex flex-col">
      <SiteHeader />

      <main className="w-full flex-1">
        <section className="relative w-full">
          <div className="absolute inset-0 h-[280px] md:h-[360px] overflow-hidden">
            <img
              src={item.backdrop}
              alt={`${item.title} 剧照背景`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/60" />
          </div>

          <div className="relative w-full px-4 md:px-8 pt-6 md:pt-8 pb-8">
            <nav className="flex items-center gap-2 text-sm text-background-100/80 mb-6">
              <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-base" />
              <Link
                to={`/category/${encodeURIComponent(item.type)}`}
                className="hover:text-accent-400 transition-colors cursor-pointer"
              >
                {item.type}
              </Link>
              <i className="ri-arrow-right-s-line text-base" />
              <span className="text-background-50 truncate">{item.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/20 bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 封面海报`}
                    title={`${item.title} - 91大神影视大全`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0 text-background-50">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                    {item.type}
                  </span>
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${encodeURIComponent(item.type)}`}
                      className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs hover:bg-background-50/25 transition-colors cursor-pointer"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>

                <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-background-50">
                  {item.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-100/90">
                  <span className="flex items-center gap-1 text-accent-400 font-semibold">
                    <i className="ri-star-fill" />
                    {item.rating.toFixed(1)}
                  </span>
                  <span>{item.year}</span>
                  <span>{item.region}</span>
                  <span>{item.episodes > 1 ? `全 ${item.episodes} 集` : item.duration}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-accent-400" />
                    {formatViews(item.views)} 次播放
                  </span>
                </div>

                <div className="mt-5 space-y-1.5 text-sm text-background-100/85">
                  <p>
                    <span className="text-background-100/60">导演：</span>
                    {item.director}
                  </p>
                  <p>
                    <span className="text-background-100/60">主演：</span>
                    {item.actors.join('、')}
                  </p>
                  <p>
                    <span className="text-background-100/60">更新时间：</span>
                    {item.updatedAt}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      fav
                        ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                        : 'bg-background-50/15 border border-background-50/40 text-background-50 hover:bg-background-50/25'
                    }`}
                  >
                    <i className={fav ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'} />
                    {fav ? '已收藏' : '收藏'}
                    <span className="text-xs opacity-70">（演示·本地存储）</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-line text-lg" />
                    分享链接
                    <span className="text-xs opacity-70">（演示）</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8">
          <div className="max-w-4xl">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              剧情简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {item.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
              <span className="text-foreground-500">所属分类：</span>
              <Link
                to={`/category/${encodeURIComponent(item.type)}`}
                className="px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
              >
                更多{item.type}
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 bg-background-50">
          <SectionHeading title="相关推荐" subtitle="根据分类与热度为您精选" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
            {recommended.map((entry) => (
              <MovieCard key={entry.id} item={entry} />
            ))}
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-primary-900 text-background-50 text-sm shadow-none">
          {toast}
        </div>
      )}

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}